import ErrorBoundary from '@/components/ErrorBoundary';
import { StandaloneLinkProps } from '@/components/element/Link';
import { DefaultComponent, StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { Content } from '@/types/content';
import { withDebugMetadata } from 'lib/utils';
import { renderContentComponent, standaloneComponent } from 'modules/dynamic-components';
import type { ReactNode } from 'react';
import { isObject, isString } from 'typesafe-utils';
import { StandaloneImageProps } from '../Image';
import { MostRead, MostReadProps } from './MostRead';
import { MostReadHeadlineProps } from './MostRead.Headline';
import { MostReadSlideProps } from './MostRead.Slide';
import { MostReadSlideCaptionProps } from './MostRead.Slide.Caption';
import { MostReadSlideDescriptionProps } from './MostRead.Slide.Description';
import { MostReadSlideGroupProps } from './MostRead.Slide.Group';
import { MostReadSlideImageProps } from './MostRead.Slide.Image';

const Link = standaloneComponent<StandaloneLinkProps>('ElementLink');
const Image = standaloneComponent<StandaloneImageProps>('ContentImage');

export interface MostReadSlide {
  caption?: ReactNode;
  description?: ReactNode;
  publishedUrl?: string;
  image?: JSX.Element;
}

export interface StandaloneMostReadProps extends StandaloneComponentProps {
  headline?: ReactNode;
  slides?: MostReadSlide[];
  options?: MostReadProps & {
    $caption?: MostReadSlideCaptionProps;
    $description?: MostReadSlideDescriptionProps;
    $group?: MostReadSlideGroupProps;
    $headline?: MostReadHeadlineProps;
    $image?: MostReadSlideImageProps;
    $slide?: MostReadSlideProps;
  };
}

export const StandaloneMostRead: StandaloneComponent<StandaloneMostReadProps> = ({
  headline,
  slides,
  options,
  ...props
}) => {
  const {
    $caption: captionProps,
    $description: descriptionProps,
    $group: groupProps,
    $headline: headlineProps,
    $image: imageProps,
    $slide: slideProps,
    ...baseProps
  } = options ?? {};

  return (
    <ErrorBoundary>
      <MostRead {...baseProps} {...props}>
        {headline && <MostRead.Headline {...headlineProps}>{headline}</MostRead.Headline>}
        <MostRead.Slide.Group {...groupProps}>
          {slides?.filter(isObject)?.map(({ caption, description, publishedUrl, image }, index) => (
            <Link
              key={index}
              href={publishedUrl}
              content={
                <MostRead.Slide {...slideProps}>
                  <MostRead.Slide.Image data-index={index} {...imageProps}>
                    {isString(image) ? <Image src={image} alt="" /> : image}
                  </MostRead.Slide.Image>
                  {caption && <MostRead.Slide.Caption {...captionProps}>{caption}</MostRead.Slide.Caption>}
                  {description && (
                    <MostRead.Slide.Description {...descriptionProps}>{description}</MostRead.Slide.Description>
                  )}
                </MostRead.Slide>
              }
            />
          ))}
        </MostRead.Slide.Group>
      </MostRead>
    </ErrorBoundary>
  );
};

const DynamicStandaloneMostRead = standaloneComponent('ContentMostRead') as typeof StandaloneMostRead;

const DefaultMostRead: DefaultComponent = ({ type, data, meta, descendants }) => {
  const slides = descendants.map((descendant: Content) => {
    const { data, meta, children } = descendant;

    return {
      caption: meta.isNativeAd ? meta.nativeAdLabel : data.category,
      description: data.title,
      publishedUrl: data.publishedUrl,
      image: children[0] ? renderContentComponent(children[0]) : undefined,
    };
  });

  if (!slides.length) {
    return null;
  }

  return (
    <DynamicStandaloneMostRead
      headline={data.title}
      slides={slides}
      data-content-index={meta.contentIndex}
      {...withDebugMetadata({ type, meta })}
    />
  );
};

export const standalone = StandaloneMostRead;

export default DefaultMostRead;
